import React from "react";
import { C } from "../theme";
import Button from "./Button";
import { suggestTripTime } from "../utils/smartTripTime";

const REPEATS = [
  { id: "once", label: "Once" },
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
];

// Local <-> input-string helpers (keep everything in the device's local zone).
function toDateStr(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function toTimeStr(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fromInputs(dateStr, timeStr) {
  if (!dateStr || !timeStr) return NaN;
  const ms = new Date(`${dateStr}T${timeStr}`).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}
function defaultStart() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(6, 0, 0, 0);
  return d;
}

export default function ScheduleTripModal({
  open,
  onClose,
  onSave,
  initialTrip = null,
  location = null,
  speciesCatalog = [],
  smartSuggestEnabled = true,
  stationLabel = "",
  stationId = "",
  stations = [],
  recentSpots = [],
  existingTrips = [],
}) {
  const isEdit = !!initialTrip?.id;

  const seedDate = React.useMemo(() => {
    if (initialTrip?.startAt?.toDate) return initialTrip.startAt.toDate();
    return defaultStart();
  }, [initialTrip]);

  const [name, setName] = React.useState("");
  const [dateStr, setDateStr] = React.useState(toDateStr(seedDate));
  const [timeStr, setTimeStr] = React.useState(toTimeStr(seedDate));
  const [repeat, setRepeat] = React.useState("once");
  const [locationLabel, setLocationLabel] = React.useState("");
  // Location picker: current GPS / a tide station / a recent session spot / custom text.
  const [locMode, setLocMode] = React.useState("current");
  const [stationSearch, setStationSearch] = React.useState("");
  const [pickedStation, setPickedStation] = React.useState(null);
  const [pickedSpot, setPickedSpot] = React.useState(null);
  const [speciesIds, setSpeciesIds] = React.useState([]);
  const [notes, setNotes] = React.useState("");
  const [suggestReason, setSuggestReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  // Seed the form once per open (or when the edit target changes). Crucially,
  // this must NOT re-seed on `location` updates: a live GPS fix arriving while
  // the modal is open would otherwise reset the date/time the user just picked
  // back to the default (tomorrow 6:00 AM) and save the wrong trip.
  const seededKeyRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) {
      seededKeyRef.current = null;
      return;
    }
    const key = initialTrip?.id || "new";
    if (seededKeyRef.current === key) return;
    seededKeyRef.current = key;
    const d = initialTrip?.startAt?.toDate ? initialTrip.startAt.toDate() : defaultStart();
    setName(initialTrip?.name || "");
    setDateStr(toDateStr(d));
    setTimeStr(toTimeStr(d));
    setRepeat(initialTrip?.repeat || "once");
    const seededLoc = initialTrip?.location || null;
    setLocationLabel(seededLoc?.label || "");
    setLocMode(
      seededLoc?.source ||
        (seededLoc ? (seededLoc.lat ? "current" : "custom") : (location?.lat ? "current" : "custom"))
    );
    setPickedStation(
      seededLoc?.stationId
        ? stations.find((s) => s.id === seededLoc.stationId) || {
            id: seededLoc.stationId,
            name: seededLoc.label,
            lat: seededLoc.lat,
            lng: seededLoc.lng,
          }
        : null
    );
    setPickedSpot(null);
    setStationSearch("");
    setSpeciesIds(Array.isArray(initialTrip?.targetSpeciesIds) ? initialTrip.targetSpeciesIds : []);
    setNotes(initialTrip?.notes || "");
    setSuggestReason("");
    setError("");
  }, [open, initialTrip, location, stations]);

  if (!open) return null;

  const toggleSpecies = (id) =>
    setSpeciesIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSmartSuggest = () => {
    const base = fromInputs(dateStr, timeStr);
    const baseDate = Number.isFinite(base) ? new Date(base) : defaultStart();
    const { at, reason } = suggestTripTime(baseDate, location);
    setDateStr(toDateStr(at));
    setTimeStr(toTimeStr(at));
    setSuggestReason(reason);
  };

  const handleSave = async () => {
    const startAtMs = fromInputs(dateStr, timeStr);
    if (!Number.isFinite(startAtMs)) {
      setError("Please pick a valid date and time.");
      return;
    }
    if (repeat === "once" && startAtMs <= Date.now()) {
      setError("That date & time is in the past.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      let loc = null;
      if (locMode === "current" && location?.lat && location?.lng) {
        loc = {
          lat: location.lat,
          lng: location.lng,
          label: (locationLabel || stationLabel || "Current location").trim(),
          stationId: stationId || null,
          source: "current",
        };
      } else if (locMode === "station" && pickedStation) {
        const stLat = pickedStation.lat ?? pickedStation.latitude ?? null;
        const stLng = pickedStation.lng ?? pickedStation.lon ?? pickedStation.longitude ?? null;
        loc = {
          lat: stLat,
          lng: stLng,
          label: (pickedStation.name || locationLabel || "Tide station").trim(),
          stationId: pickedStation.id || null,
          source: "station",
        };
      } else if (locMode === "recent" && pickedSpot) {
        loc = {
          lat: pickedSpot.lat ?? null,
          lng: pickedSpot.lng ?? null,
          label: (pickedSpot.label || "Recent spot").trim(),
          stationId: pickedSpot.stationId ?? null,
          source: "recent",
        };
      } else if (locationLabel.trim()) {
        loc = { lat: null, lng: null, label: locationLabel.trim(), source: "custom" };
      }
      await onSave({
        id: initialTrip?.id,
        notificationId: initialTrip?.notificationId,
        name,
        startAtMs,
        location: loc,
        targetSpeciesIds: speciesIds,
        notes,
        repeat,
        smartSuggested: !!suggestReason,
      });
      onClose();
    } catch (err) {
      setError(err?.message || "Couldn't save the trip. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%",
    background: C.midnight,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    color: C.white,
    fontSize: 14,
    padding: "11px 12px",
    boxSizing: "border-box",
    colorScheme: "dark",
  };
  const flabel = { fontSize: 11, color: C.sand, marginBottom: 6, display: "block", fontWeight: 600 };

  // Location picker options (only offer modes that have data).
  const locModes = [
    ...(location?.lat ? [{ id: "current", label: "Current" }] : []),
    ...(stations.length ? [{ id: "station", label: "Station" }] : []),
    ...(recentSpots.length ? [{ id: "recent", label: "Recent" }] : []),
    { id: "custom", label: "Custom" },
  ];
  const stQuery = stationSearch.trim().toLowerCase();
  const filteredStations = (stQuery
    ? stations.filter((s) => (s.name || "").toLowerCase().includes(stQuery))
    : stations
  ).slice(0, 12);
  const pickBtnStyle = (on) => ({
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    textAlign: "left",
    padding: "8px 10px",
    borderRadius: 10,
    border: `1px solid ${on ? C.seafoam : C.border}`,
    background: on ? "rgba(46,191,165,0.12)" : "transparent",
    color: C.white,
    cursor: "pointer",
    marginBottom: 6,
  });

  // Non-blocking heads-up: does the angler already have a trip that same day?
  // (daily repeats hit every day; weekly repeats hit the same weekday.)
  const sameDayTrips = existingTrips.filter((t) => {
    if (initialTrip?.id && t.id === initialTrip.id) return false;
    const d = t.startAt?.toDate ? t.startAt.toDate() : null;
    if (!d) return false;
    const rep = t.repeat || "once";
    if (rep === "daily") return true;
    if (!dateStr) return false;
    const sel = new Date(`${dateStr}T00:00`);
    if (Number.isNaN(sel.getTime())) return false;
    if (rep === "weekly") return d.getDay() === sel.getDay();
    return toDateStr(d) === dateStr;
  });
  const conflictLabel = (() => {
    const t = sameDayTrips[0];
    if (!t) return "";
    const d = t.startAt?.toDate ? t.startAt.toDate() : null;
    const time = d ? new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(d) : "";
    return `${time}${t.name ? ` \u00b7 ${t.name}` : ""}`.trim();
  })();

  const Segmented = ({ options, value, onChange, getKey = (o) => o.id, getLabel = (o) => o.label }) => (
    <div style={{ display: "flex", background: C.midnight, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
      {options.map((o) => {
        const k = getKey(o);
        const on = k === value;
        return (
          <div
            key={k}
            onClick={() => onChange(k)}
            style={{
              flex: 1,
              textAlign: "center",
              fontSize: 11,
              padding: "9px 0",
              cursor: "pointer",
              color: on ? "#fff" : C.muted,
              background: on ? C.seafoam : "transparent",
              fontWeight: on ? 700 : 400,
            }}
          >
            {getLabel(o)}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: 375,
        background: "rgba(4,8,15,0.72)",
        zIndex: 1200,
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          marginTop: "auto",
          background: C.deepWater,
          borderTop: `1px solid ${C.border}`,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          maxHeight: "92%",
          display: "flex",
          flexDirection: "column",
          boxShadow: C.menuShadow,
        }}
      >
        <div style={{ padding: "16px 18px 10px", display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 3, color: C.teal, textTransform: "uppercase" }}>Schedule</div>
            <div style={{ fontFamily: "Georgia,serif", fontWeight: 700, fontSize: 20, color: C.white }}>
              {isEdit ? "Edit Trip" : "Plan a Trip"}
            </div>
          </div>
          <button onClick={onClose} type="button" aria-label="Close" style={{ background: "none", border: "none", color: C.muted, fontSize: 22, cursor: "pointer" }}>
            ✕
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "4px 18px 18px" }}>
          <div style={{ marginBottom: 12 }}>
            <label style={flabel}>Trip name (optional)</label>
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sunrise bite" />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={flabel}>Date &amp; time</label>
            <div style={{ display: "flex", gap: 10 }}>
              <input type="date" style={{ ...inputStyle, flex: 1 }} value={dateStr} onChange={(e) => { setDateStr(e.target.value); setSuggestReason(""); }} />
              <input type="time" style={{ ...inputStyle, flex: 1 }} value={timeStr} onChange={(e) => { setTimeStr(e.target.value); setSuggestReason(""); }} />
            </div>
            {smartSuggestEnabled && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={handleSmartSuggest}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: `${C.gold}1F`,
                    border: `1px solid ${C.gold}66`,
                    color: C.gold,
                    borderRadius: 999,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  ✨ Suggest best time
                </button>
                {suggestReason && (
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.4 }}>{suggestReason}</div>
                )}
              </div>
            )}
            {sameDayTrips.length > 0 && (
              <div
                data-testid="schedule-sameday-warning"
                style={{ marginTop: 8, fontSize: 11, color: C.gold, background: `${C.gold}14`, border: `1px solid ${C.gold}55`, borderRadius: 10, padding: "8px 10px", lineHeight: 1.4 }}
              >
                ⚠️ You already have {sameDayTrips.length > 1 ? `${sameDayTrips.length} trips` : "a trip"} scheduled that day{conflictLabel ? ` (${conflictLabel})` : ""}. You can still add this one.
              </div>
            )}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={flabel}>Repeat</label>
            <Segmented options={REPEATS} value={repeat} onChange={setRepeat} />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
              🔔 You'll get a notification when this session starts.
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={flabel}>Location</label>
            <Segmented options={locModes} value={locMode} onChange={setLocMode} />
            <div style={{ marginTop: 8 }}>
              {locMode === "current" && (
                <div style={{ fontSize: 12, color: C.sand, padding: "8px 2px", display: "flex", alignItems: "center", gap: 6 }}>
                  📍 {stationLabel ? `${stationLabel} (nearest station)` : (location?.lat ? "Your current location" : "No GPS fix yet")}
                </div>
              )}
              {locMode === "station" && (
                <>
                  <input
                    style={inputStyle}
                    value={stationSearch}
                    onChange={(e) => setStationSearch(e.target.value)}
                    placeholder="Search tide stations…"
                  />
                  <div style={{ maxHeight: 160, overflowY: "auto", marginTop: 8 }}>
                    {filteredStations.length === 0 ? (
                      <div style={{ fontSize: 11, color: C.muted, padding: 8 }}>
                        {stations.length ? "No stations match." : "Finding nearby stations\u2026"}
                      </div>
                    ) : (
                      filteredStations.map((st) => (
                        <button
                          key={st.id}
                          type="button"
                          onClick={() => { setPickedStation(st); setLocationLabel(st.name || ""); }}
                          style={pickBtnStyle(pickedStation?.id === st.id)}
                        >
                          <span style={{ fontSize: 11, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{st.name}</span>
                          {typeof st.geoDist === "number" && (
                            <span style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>{st.geoDist.toFixed(1)} mi</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
              {locMode === "recent" && (
                <div style={{ maxHeight: 160, overflowY: "auto" }}>
                  {recentSpots.length === 0 ? (
                    <div style={{ fontSize: 11, color: C.muted, padding: 8 }}>No recent spots yet.</div>
                  ) : (
                    recentSpots.map((sp) => (
                      <button
                        key={sp.key}
                        type="button"
                        onClick={() => { setPickedSpot(sp); setLocationLabel(sp.label || ""); }}
                        style={pickBtnStyle(pickedSpot?.key === sp.key)}
                      >
                        <span style={{ fontSize: 11, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sp.label}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
              {locMode === "custom" && (
                <input
                  style={inputStyle}
                  value={locationLabel}
                  onChange={(e) => setLocationLabel(e.target.value)}
                  placeholder="Name this spot"
                />
              )}
            </div>
          </div>

          {speciesCatalog.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={flabel}>Target species</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 132, overflowY: "auto" }}>
                {speciesCatalog.map((sp) => {
                  const on = speciesIds.includes(sp.id);
                  return (
                    <span
                      key={sp.id}
                      onClick={() => toggleSpecies(sp.id)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "7px 11px",
                        borderRadius: 999,
                        fontSize: 12,
                        cursor: "pointer",
                        border: `1px solid ${on ? C.seafoam : C.border}`,
                        color: on ? "#fff" : C.sand,
                        background: on ? "rgba(37,99,235,0.22)" : C.midnight,
                      }}
                    >
                      {sp.label}{on ? " ✓" : ""}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={flabel}>Notes</label>
            <textarea
              style={{ ...inputStyle, height: 64, resize: "none", fontFamily: "inherit" }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Gear, tactics, tide notes…"
            />
          </div>

          {error && <div style={{ fontSize: 12, color: C.warning, marginBottom: 10 }}>{error}</div>}

          <Button variant="primary" fullWidth disabled={saving} onClick={handleSave}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Schedule Trip"}
          </Button>
          <div style={{ height: 8 }} />
          <Button variant="ghost" fullWidth disabled={saving} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
